import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import {
  GameClock,
  setInputOffsetMs,
  setAudioOffsetMs,
  getAudioOffsetMs,
} from '../timing';

/**
 * Overlay widget guiding the user through input and audio latency calibration.
 */
export default function CalibrationWidget({ onComplete }: { onComplete: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const beatTimesRef = useRef<number[]>([]);
  const tapDiffsRef = useRef<number[]>([]);
  const tapYsRef = useRef<number[]>([]);
  const meanLineYRef = useRef<number | null>(null);
  // Use a performance.now() timebase for animation to avoid relying on
  // the global audio clock, which may stop after calibration runs. This
  // ensures the ball always animates even if the AudioContext is closed.
  const startRef = useRef<number>(0);
  const lastProgRef = useRef<number>(0);
  const [phase, setPhase] = useState<'input' | 'audio'>('input');
  const [audioOffset, setAudioOffset] = useState(getAudioOffsetMs());
  const [started, setStarted] = useState(false);
  const inputOffsetRef = useRef(0);
  // Vertical line representing where the raw tap was received.
  const inputLineYRef = useRef<number | null>(null);
  const inputLineColorRef = useRef<string>('white');
  // Vertical line representing tap position after applying calibration (yellow).
  const calibratedLineYRef = useRef<number | null>(null);
  const stableRef = useRef(0);
  const MAX_TAPS = 8;

  const bpm = 120;
  const interval = 60000 / bpm; // ms per beat

  // Animation loop for bouncing ball and beat tracking
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    // Reset animation state using performance.now() as the timebase.
    startRef.current = performance.now();
    lastProgRef.current = 0;
    inputLineYRef.current = null;
    calibratedLineYRef.current = null;
    meanLineYRef.current = null;
    if (phase === 'input') {
      beatTimesRef.current = [];
      tapDiffsRef.current = [];
      tapYsRef.current = [];
      stableRef.current = 0;
    }
    let frame = 0;
    const render = () => {
      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      const floor = height - 20;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, floor, width, 2);
      const radius = 10;
      const amplitude = floor - radius - 20;

      let y = floor - amplitude;
      let ballColor = 'white';
      if (phase === 'audio' || started) {
        const now = performance.now();
        const elapsed = now - startRef.current;
        const progress = (elapsed % interval) / interval;
        if (phase === 'input' && progress < lastProgRef.current) {
          beatTimesRef.current.push(now);
        }
        lastProgRef.current = progress;
        y = floor - amplitude * Math.abs(Math.sin(Math.PI * progress));
        ballColor = progress > 0.5 ? 'blue' : 'red';
      }

      ctx.beginPath();
      ctx.fillStyle = ballColor;
      ctx.arc(width / 2, y, radius, 0, Math.PI * 2);
      ctx.fill();

      // draw current offset text
      if (phase === 'input') {
        ctx.fillStyle = inputLineColorRef.current || 'white';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`${inputOffsetRef.current}ms`, width - 4, 16);
      }

      // draw tap position lines
      if (phase === 'input') {
        if (inputLineYRef.current !== null) {
          ctx.strokeStyle = inputLineColorRef.current;
          ctx.beginPath();
          ctx.moveTo(0, inputLineYRef.current);
          ctx.lineTo(width, inputLineYRef.current);
          ctx.stroke();
        }
        if (meanLineYRef.current !== null) {
          ctx.strokeStyle = inputLineColorRef.current;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(0, meanLineYRef.current);
          ctx.lineTo(width, meanLineYRef.current);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        if (calibratedLineYRef.current !== null) {
          ctx.strokeStyle = 'yellow';
          ctx.beginPath();
          ctx.moveTo(0, calibratedLineYRef.current);
          ctx.lineTo(width, calibratedLineYRef.current);
          ctx.stroke();
        }
      }

      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, [phase, started]);

  const handleTap = () => {
    if (!started) {
      // First tap starts the animation using a fresh performance.now() origin.
      startRef.current = performance.now();
      setStarted(true);
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const taps = tapDiffsRef.current;
    const beats = beatTimesRef.current;
    if (!beats.length) return;
    const now = performance.now();
    const lastBeat = beats[beats.length - 1];
    taps.push(now - lastBeat);
    if (taps.length > MAX_TAPS) taps.shift();
    const avg = taps.reduce((a, b) => a + b, 0) / taps.length;
    const candidate = Math.round(avg);

    // compute raw ball position at the moment of the tap (no calibration)
    const height = canvas.height;
    const floor = height - 20;
    const radius = 10;
    const amplitude = floor - radius - 20;
    const elapsedNow = now - startRef.current;
    const progressNow = (elapsedNow % interval) / interval;
    const rawY = floor - amplitude * Math.abs(Math.sin(Math.PI * progressNow));
    inputLineYRef.current = rawY;
    inputLineColorRef.current = progressNow > 0.5 ? 'blue' : 'red';
    const ys = tapYsRef.current;
    ys.push(rawY);
    if (ys.length > MAX_TAPS) ys.shift();
    meanLineYRef.current = ys.reduce((a, b) => a + b, 0) / ys.length;

    // compute expected ball position based on timing difference
    const prevCal = inputOffsetRef.current;
    const timeYellow = candidate - prevCal;
    const moduloTime = ((timeYellow % interval) + interval) % interval;
    const progress = moduloTime / interval;
    const calibratedY = floor - amplitude * Math.abs(Math.sin(Math.PI * progress));
    const dist = Math.abs(calibratedY - floor);

    inputOffsetRef.current = candidate;
    calibratedLineYRef.current = calibratedY;
    if (dist < 2) {
      stableRef.current += 1;
    } else {
      stableRef.current = 0;
    }

    if (stableRef.current >= 3) {
      setInputOffsetMs(candidate);
      setPhase('audio');
      tapDiffsRef.current = [];
      beatTimesRef.current = [];
      stableRef.current = 0;
      setStarted(true); // ensure animation continues in audio phase
    }
  };

  // Schedule metronome clicks during audio phase
  useEffect(() => {
    if (phase !== 'audio') return;
    let active = true;
    GameClock.ready.then(() => {
      const ctx = GameClock.context!;
      void ctx.resume();
      const intervalSec = 60 / bpm;
      let next = ctx.currentTime + Math.max(0, audioOffset) / 1000;
      const scheduleClick = (time: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 1000;
        gain.gain.setValueAtTime(1, time);
        gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
        osc.connect(gain).connect(ctx.destination);
        osc.start(time);
        osc.stop(time + 0.05);
      };
      const scheduler = () => {
        if (!active) return;
        while (next < ctx.currentTime + 0.1) {
          scheduleClick(next);
          next += intervalSec;
        }
        GameClock.schedule(GameClock.now() / 1000 + 0.025, scheduler);
      };
      scheduler();
    });
    return () => {
      active = false;
    };
  }, [phase, audioOffset]);

  const finish = () => {
    setAudioOffsetMs(audioOffset);
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center space-y-4 bg-black/80 text-white">
      <canvas
        ref={canvasRef}
        width={200}
        height={200}
        onClick={phase === 'input' ? handleTap : undefined}
        className="border border-white mb-4"
      />
      {phase === 'input' ? (
        <p>Click when the ball hits the floor.</p>
      ) : (
        <>
          <p>Align the click sound with the ball.</p>
          <div className="flex items-center gap-2">
            {[-20, -5, -1, 1, 5, 20].map((n) => (
              <Button key={n} variant="outline" size="sm" onClick={() => setAudioOffset(audioOffset + n)}>
                {n > 0 ? `+${n}` : n}
              </Button>
            ))}
            <span className="w-16 text-center">{audioOffset}ms</span>
          </div>
          <Button onClick={finish}>Done</Button>
        </>
      )}
    </div>
  );
}
